import './MathDecisionCard.css';
import type { ComponentChildren, RefObject } from 'preact';
import { useId, useLayoutEffect, useRef } from 'preact/hooks';
import { useReducedMotion } from '../../a11y/useReducedMotion';
import { Button } from '../../components/Button';
import { PartyTag } from '../../components/PartyTag';
import { Portrait } from '../../components/Portrait';
import { tMathCard } from '../../copy/mathCard';
import { t } from '../../copy/strings';
import { fmtPct, fmtSections } from '../../format/numbers';
import { fmtTime } from '../../format/time';
import { epochMs, tMinOfIso } from '../../model/time';
import type { Decision, ResultsView } from '../../model/types';
import { partyClass, partyOf } from '../../theme/parties';

/**
 * Cartão da definição matemática em tela cheia, no celular e no computador (D4, ORD-F-078/082). A versão da TV é o
 * `EventOverlay` (§12.4); este é o do site ao vivo, fora da TV. Pedaço `mathDecisionCard`, carregado só com
 * `features.math_card` e só quando a decisão matemática nacional do presidente chega (src/app/mathCardSlot.tsx decide).
 *
 * Linguagem (regra 3 do CLAUDE.md): o título é o mesmo do herói no estado `decided` (`headline.elected`, H6); a linha de
 * cima diz "Conta dos votos · {HHhMM}" (hora de `decision.at` em Brasília, pelos formatadores de src/format).
 * Nunca "Eleito · TSE": com o resultado do TSE publicado o cartão não abre. Textos novos em src/copy/mathCard.ts.
 *
 * Modal (§15.2.5): `role="dialog"` + `aria-modal`, `inert` no `#app-root`, rolagem travada, foco no título ao abrir,
 * `Tab` preso no cartão e o foco de volta a quem o tinha ao fechar. Fecha pelo botão, pelo Esc e pelo toque no véu fora
 * do cartão. A camada do Esc (cascata única, src/a11y/escape.ts) fica no slot, que guarda o estado aberto: com o cartão
 * importando `escape.ts` e `state/hooks.ts`, o Rolldown tirava o núcleo do index num chunk comum (`hooks-*.js`, DE-C20);
 * pela mesma razão, `photos` chega por prop. O anúncio ao leitor de tela é o da `LiveRegion` única (o
 * evento `math_decision` de saliência 3, §15.3); o cartão não cria outra região viva. Movimento reduzido: sem animação.
 */

/** Irmão do portal que recebe `inert` enquanto o cartão está aberto (§15.2.5; o mesmo do AboutDialog). */
const APP_ROOT_ID = 'app-root';
/** Retrato: o tamanho do herói (§7.5 bloco 6), o mesmo nos dois lados. */
const PORTRAIT_PX = 52;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Os textos do cartão (o que se vê e o que o Compartilhar do FRONT-2 vai enviar). */
export interface MathCardText {
  title: string;
  kicker: string;
  sub: string;
  /** [número menor, número maior]: nome, sigla e percentual dos válidos, na ordem do contrato (nunca pelo líder). */
  sides: readonly [MathCardSideText, MathCardSideText];
  /** Texto corrido para o `share()` (src/state/actions.ts) do FRONT-2. */
  share: string;
}
export interface MathCardSideText {
  name: string;
  pct: string;
}

/** Pura. Os textos do cartão para a vista e a decisão matemática dadas. */
export function mathCardText(view: ResultsView, decision: Decision, electionDate: string): MathCardText {
  const winner = view.candidates.find((c) => c.number === decision.winner) ?? null;
  const hhmm = fmtTime(tMinOfIso(decision.at, epochMs(electionDate)), electionDate);
  const n = view.national;
  const title = t('headline.elected', { X: winner?.display_name ?? null, 'X.gender': winner?.gender ?? null });
  const [a, b] = view.candidates;
  const sides = [
    { name: a.display_name, pct: fmtPct(n.shares[0], 2) },
    { name: b.display_name, pct: fmtPct(n.shares[1], 2) },
  ] as const;
  return {
    title,
    kicker: tMathCard('mathCard.kicker', { HHhMM: hhmm }),
    sub: t('event.mathDecision.overlaySub', { S: fmtSections(n.sectionsFraction) }),
    sides,
    share: tMathCard('mathCard.share.text', {
      TITLE: title,
      HHhMM: hhmm,
      A: sides[0].name,
      pA: sides[0].pct,
      B: sides[1].name,
      pB: sides[1].pct,
    }),
  };
}

export interface MathDecisionCardProps {
  /** Vista ao vivo (os percentuais acompanham os snapshots enquanto o cartão está aberto). */
  view: ResultsView;
  /** A decisão matemática nacional (`national.mathDecision`) que abriu o cartão. */
  decision: Decision;
  electionDate: string;
  /** `features.photos`. */
  photos: boolean;
  onClose(): void;
  /**
   * Ponto de encaixe do botão Compartilhar do FRONT-2 (ele chama `share()` com `text`). Sem a prop, nada: o botão não é
   * criado aqui.
   */
  share?: (text: string) => ComponentChildren;
}

export function MathDecisionCard({ view, decision, electionDate, photos, onClose, share }: MathDecisionCardProps) {
  const reduced = useReducedMotion();
  const boxRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const id = useId();
  const titleId = `${id}-t`;
  const kickerId = `${id}-k`;
  useModal(boxRef, titleRef);
  const text = mathCardText(view, decision, electionDate);
  const n = view.national;
  return (
    <div
      class={`math-card${reduced ? ' math-card--still' : ''}`}
      data-testid="math-card"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={boxRef}
        class="math-card__box"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={kickerId}
        onKeyDown={(e) => trapTab(e, boxRef.current)}
      >
        <p id={kickerId} class="math-card__kicker" data-testid="math-card-kicker">
          {text.kicker}
        </p>
        <h2 ref={titleRef} id={titleId} class="math-card__title" tabIndex={-1} data-testid="math-card-title">
          {text.title}
        </h2>
        <p class="math-card__sub">{text.sub}</p>
        <ul class="math-card__sides" aria-label={tMathCard('mathCard.sides.aria')}>
          {view.candidates.map((c, i) => (
            <li
              key={c.number}
              class={`math-card__side ${partyClass(partyOf(c))}`}
              data-number={c.number}
              data-testid="math-card-side"
            >
              <span class="math-card__portrait">
                <Portrait candidate={c} photo={photos ? c.photo : null} size={PORTRAIT_PX} />
              </span>
              <span class="math-card__name">{c.display_name}</span>
              <PartyTag candidate={c} />
              <span class="math-card__pct" data-dataviz="">
                {fmtPct(n.shares[i as 0 | 1], 2)}
              </span>
            </li>
          ))}
        </ul>
        <p class="math-card__source">{tMathCard('mathCard.source')}</p>
        <div class="math-card__actions">
          {share ? share(text.share) : null}
          <Button label={t('common.close')} class="math-card__close" onClick={onClose} />
        </div>
      </div>
    </div>
  );
}

// ── Comportamento modal (§15.2.5) ────────────────────────────────────────────────────────────────────────────

/** `Tab` e `Shift+Tab` circulam dentro do cartão (o `inert` do `#app-root` já tira o resto da página). */
function trapTab(e: KeyboardEvent, box: HTMLElement | null): void {
  if (e.key !== 'Tab' || !box) return;
  const items = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)];
  if (items.length === 0) {
    e.preventDefault();
    return;
  }
  const first = items[0] as HTMLElement;
  const last = items[items.length - 1] as HTMLElement;
  const active = document.activeElement;
  const inside = active instanceof HTMLElement && items.includes(active);
  if (e.shiftKey && (active === first || !inside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !inside)) {
    e.preventDefault();
    first.focus();
  }
}

function focusable(el: Element | null): el is HTMLElement {
  return el !== null && typeof (el as HTMLElement).focus === 'function';
}

/** inert no irmão, rolagem travada, foco no título; ao desmontar, desfaz tudo e devolve o foco a quem o tinha. */
function useModal(boxRef: RefObject<HTMLDivElement>, titleRef: RefObject<HTMLHeadingElement>): void {
  useLayoutEffect(() => {
    const doc = document;
    const active = doc.activeElement;
    // capturado antes do inert: o elemento focado dentro de #app-root perde o foco quando o contêiner fica inerte
    const opener =
      focusable(active) && active !== doc.body && !boxRef.current?.contains(active) ? (active as HTMLElement) : null;
    const appRoot = doc.getElementById(APP_ROOT_ID);
    const ownsInert = appRoot !== null && !appRoot.hasAttribute('inert');
    if (ownsInert) appRoot.setAttribute('inert', '');
    const html = doc.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    titleRef.current?.focus({ preventScroll: true });
    return () => {
      if (ownsInert) appRoot.removeAttribute('inert');
      html.style.overflow = prevOverflow;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);
}
